/**
 * Shared link-preview helpers — pure, no thunk/store imports so both the
 * send tool (WhatsappSendMessageThunk) and the diagnostics runner can use
 * them without circular-dependency risk.
 *
 * Two consumers, one shape:
 *   - sendMessageThunkTool resolves a preview at SEND time (direct sends, and
 *     the scheduler's eventual send) and attaches it to the WA message.
 *   - The diagnostics SCHEDULE path resolves a preview at CREATE time and
 *     stores it on the userMessage doc so the scheduled-messages UI renders
 *     the card (matching the product editor's "Update Link Preview" flow).
 */

/** Raw shape the engine's `waGetLinkPreviewTool` returns. */
export interface EngineLinkPreviewResult {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    matchedText?: string;
    /** Raw base64 (no data: prefix) or a data URL. */
    thumbnail?: string;
    thumbnailHQ?: string;
    [key: string]: unknown;
}

const URL_IN_TEXT_REGEX = /https?:\/\/[^\s<>"']+/i;

/** First http(s) URL in `text` (trailing prose punctuation trimmed), or ''. */
export function findFirstUrlInText(text: string): string {
    const match = String(text ?? '').match(URL_IN_TEXT_REGEX);
    return match ? match[0].replace(/[.,;:!?)\]\}>"']+$/, '') : '';
}

/** Wrap raw base64 (or pass a data URL through) for the page link-preview shape. */
export function normalizeLinkPreviewThumbnail(thumbnail?: string): string {
    if (!thumbnail) return '';
    return thumbnail.startsWith('data:image/') ? thumbnail : `data:image/jpeg;base64,${thumbnail}`;
}

/**
 * Build the persisted/sendable `linkPreview` object from an engine result.
 *
 * `title` drives WhatsApp's rich-card render; fall back to the URL hostname
 * so a basic card always shows even when the crawler returned a sparse result
 * (mirrors the public-API send path's `toUserMessageLinkPreview`). The same
 * object is what the scheduled-messages UI reads (title/description/thumbnail/
 * url) and what `prepareMessageForWa` consumes on send (url/title/thumbnail).
 */
export function buildLinkPreview(fetched: EngineLinkPreviewResult | null, url: string): Record<string, unknown> {
    const canonicalUrl = fetched?.canonicalUrl || fetched?.matchedText || url;
    let hostname = canonicalUrl;
    try { hostname = new URL(canonicalUrl).hostname; } catch { /* keep canonicalUrl */ }

    return {
        ...(fetched || {}),
        title: String(fetched?.title ?? '').trim() || hostname,
        description: fetched?.description ?? '',
        url: canonicalUrl,
        link: canonicalUrl,
        matchedText: fetched?.matchedText || canonicalUrl,
        thumbnail: normalizeLinkPreviewThumbnail(fetched?.thumbnail),
        thumbnailHQ: normalizeLinkPreviewThumbnail(fetched?.thumbnailHQ),
    };
}
